// frontend/src/components/admin/UserRegistrationForm.js
import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../AuthProvider';

const UserRegistrationForm = ({ onClose }) => {
  const { token } = useAuth();

  // ==== state daftar users (kiri) ====
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [search, setSearch] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  // user terpilih utk edit (kanan). null = new user.
  const [selected, setSelected] = useState(null);

  // ==== delete modal state ====
  const [deleteTarget, setDeleteTarget] = useState(null); // user object yg mau dihapus
  const [deleting, setDeleting] = useState(false);  

  // ==== state form (kanan) ====
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [systemRole, setSystemRole] = useState('user');
  const [clusterId, setClusterId] = useState('');
  const [clusterRole, setClusterRole] = useState('member');
  const [clusters, setClusters] = useState([]);
  const [formMsg, setFormMsg] = useState('');
  const [formErr, setFormErr] = useState('');
  const [loadingClusters, setLoadingClusters] = useState(true);

  // ========= helpers =========
  const fetchUsers = async () => {
    setUsersLoading(true);
    setUsersError('');
    try {
      const res = await fetch('/api/admin/users', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Gagal mengambil daftar user');
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : (data.items || []));
    } catch (err) {
      setUsersError(`Terjadi kesalahan: ${err.message}`);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); /* eslint-disable-next-line */ }, [token, refreshKey]);

  // Clusters untuk form
  useEffect(() => {
    (async () => {
      setLoadingClusters(true);
      try {
        const r = await fetch('/api/all-clusters', { headers: { Authorization: `Bearer ${token}` }});
        if (!r.ok) throw new Error((await r.json()).detail || 'Gagal mengambil daftar cluster');
        const data = await r.json();
        setClusters(data || []);
        if (data?.length && !clusterId) setClusterId(String(data[0].id));
      } catch (e) {
        setFormErr(`Gagal memuat cluster: ${e.message}`);
      } finally {
        setLoadingClusters(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Saat pilih user di tabel → isi form untuk edit
  useEffect(() => {
    if (!selected) {
      // mode register baru
      setEmail('');
      setName('');
      setSystemRole('user');
      setClusterRole('member');
      if (clusters?.length) setClusterId(String(clusters[0].id));
      setFormMsg('');
      setFormErr('');
      return;
    }
    const u = selected;
    setEmail(u.email || '');
    setName(u.name || '');
    setSystemRole(u.role || 'user');
    // default cluster utk tambah membership cepat
    if (clusters?.length) setClusterId(String(clusters[0].id));
    setClusterRole('member');
    setFormMsg('');
    setFormErr('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, clusters]);

  // ======== toggle status aktif ========
  const toggleActive = async (userId, current) => {
    try {
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, is_active: !current } : u)));
      const res = await fetch(`/api/admin/users/${userId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_active: !current }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Gagal mengubah status');
    } catch (err) {
      setUsersError(`Gagal update status: ${err.message}`);
      fetchUsers();
    }
  };

  // ======== delete user ========
  const deleteUser = async (user) => {
    if (!user?.id) return;

    // Guard: jangan delete admin (opsional)
    if ((user.role || "").toLowerCase() === "admin") {
      setUsersError("Tidak bisa menghapus user dengan role admin.");
      return;
    }

    setDeleting(true);
    setUsersError("");

    try {
      // ✅ Endpoint yang akan kamu buat di backend:
      // DELETE /api/admin/users/{user_id}
      const res = await fetch(`/api/admin/users/${user.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      // Kalau backend belum ada, biasanya 404/405
      if (!res.ok) {
        let msg = "";
        try {
          const j = await res.json();
          msg = j?.detail || "";
        } catch (_) {}
        throw new Error(msg || `Endpoint delete belum tersedia / gagal (HTTP ${res.status})`);
      }

      // ✅ Update UI
      setUsers((prev) => prev.filter((u) => u.id !== user.id));

      // kalau sedang selected user itu → reset form
      if (selected?.id === user.id) setSelected(null);

      setDeleteTarget(null);
    } catch (err) {
      setUsersError(`Gagal delete user: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };
  

  // ======== submit form (register/edit via upsert) ========
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormMsg(''); setFormErr('');
    try {
      const res = await fetch('/api/admin/register-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          email,
          name,
          role: systemRole,
          cluster_id: Number(clusterId),
          cluster_role: clusterRole,
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'Gagal menyimpan');
      setFormMsg('✅ Tersimpan');
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setFormErr(`❌ ${err.message}`);
    }
  };

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const membershipText = (u.memberships || [])
        .map((m) => `${m.cluster_name} ${m.cluster_role}`).join(' ').toLowerCase();
      return (
        (u.email || '').toLowerCase().includes(q) ||
        (u.name || '').toLowerCase().includes(q) ||
        (u.role || '').toLowerCase().includes(q) ||
        membershipText.includes(q)
      );
    });
  }, [users, search]);

  const roleBadgeCls = (role) =>
    role === 'admin'
      ? 'px-2 py-0.5 rounded-full text-xs bg-red-900/30 border border-red-700/40 text-red-300'
      : 'px-2 py-0.5 rounded-full text-xs bg-blue-900/30 border border-blue-700/40 text-blue-300';

  const statusBadge = (active) => (
    <span className={`px-2 py-0.5 rounded-full text-xs border ${active
      ? 'bg-green-900/30 border-green-700/40 text-green-300'
      : 'bg-gray-700/40 border-gray-600 text-gray-300'}`}>
      {active ? 'Active' : 'Inactive'}
    </span>
  );

  const closePanel = () => onClose && onClose();

  return (
    <div className="h-[85vh] mx-auto max-w-7xl bg-gray-900 text-white rounded-xl border border-gray-700 shadow-2xl mt-10 flex flex-col">
      {/* Header sticky */}
      <div className="px-6 py-4 border-b border-gray-800 sticky top-0 bg-gray-900/95 backdrop-blur rounded-t-xl z-10">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl font-bold text-blue-400">User Management</h1>
          <div className="flex items-center gap-2">
            <input
              value={search}
              onChange={(e)=>setSearch(e.target.value)}
              placeholder="Cari user…"
              className="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={()=>setSelected(null)}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg"
              title="Register User Baru"
            >
              New User
            </button>
            {onClose && (
              <button onClick={closePanel} className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg">
                Close
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Grid 2 kolom */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 grow overflow-hidden">
        {/* Kiri: Tabel Users */}
        <div className="overflow-auto border-r border-gray-800">
          {usersError && <p className="m-4 text-sm text-red-400 bg-red-900/30 p-2 rounded">{usersError}</p>}
          {usersLoading ? (
            <p className="p-4 text-blue-300">Memuat users…</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-800/60 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-2 text-left text-sm text-gray-300">Email</th>
                  <th className="px-4 py-2 text-left text-sm text-gray-300">Role</th>
                  <th className="px-4 py-2 text-left text-sm text-gray-300">Status</th>
                  <th className="px-4 py-2 text-right text-sm text-gray-300">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800 bg-gray-900">
                {filteredUsers.length === 0 && (
                  <tr><td className="px-4 py-6 text-center text-gray-400" colSpan={4}>Tidak ada data.</td></tr>
                )}
                {filteredUsers.map((u) => (
                  <tr
                    key={u.id}
                    className="hover:bg-gray-800/40 cursor-pointer"
                    onClick={() => setSelected(u)}
                    title="Klik untuk edit di panel kanan"
                  >
                    <td className="px-4 py-2 text-sm">
                      <div className="font-medium">{u.email}</div>
                      <div className="text-xs text-gray-400">{u.name || '—'}</div>
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <span className={roleBadgeCls(u.role)}>{u.role}</span>
                    </td>
                    <td className="px-4 py-2 text-sm">{statusBadge(u.is_active)}</td>

                    <td className="px-4 py-2 text-sm text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={(e)=>{ e.stopPropagation(); toggleActive(u.id, u.is_active); }}
                          className={`px-3 py-1 rounded-lg border ${
                            u.is_active
                              ? 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                              : 'bg-green-700/70 border-green-600 hover:bg-green-700'
                          }`}
                        >
                          {u.is_active ? 'Set Inactive' : 'Set Active'}
                        </button>
                    
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteTarget(u); }}
                          disabled={(u.role || "").toLowerCase() === "admin"}
                          className={`px-3 py-1 rounded-lg border text-white ${
                            (u.role || "").toLowerCase() === "admin"
                              ? "bg-red-900/20 border-red-700/30 opacity-50 cursor-not-allowed"
                              : "bg-red-600/80 border-red-500 hover:bg-red-600"
                          }`}
                          title={(u.role || "").toLowerCase() === "admin" ? "Admin tidak bisa dihapus" : "Delete user"}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                      
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Kanan: Form Register / Edit */}
        <div className="overflow-auto">
          <div className="p-6">
            <div className="mb-4">
              <h2 className="text-xl font-semibold">
                {selected ? 'Edit User' : 'Register User Baru'}
              </h2>
              {formErr && <p className="mt-2 text-sm text-red-400 bg-red-900/30 p-2 rounded">{formErr}</p>}
              {formMsg && <p className="mt-2 text-sm text-green-400 bg-green-900/30 p-2 rounded">{formMsg}</p>}
            </div>

            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1 text-gray-300">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e)=>setEmail(e.target.value)}
                  required
                  disabled={!!selected} // email jadi key saat edit
                  className="w-full px-4 py-2 bg-gray-800 rounded-lg border border-gray-700 focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Masukkan email user"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-300">Nama</label>
                <input
                  value={name}
                  onChange={(e)=>setName(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-800 rounded-lg border border-gray-700 focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Nama user (opsional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-300">System Role</label>
                <select
                  value={systemRole}
                  onChange={(e)=>setSystemRole(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-800 rounded-lg border border-gray-700 focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-300">Pilih Cluster</label>
                <select
                  value={clusterId}
                  onChange={(e)=>setClusterId(e.target.value)}
                  disabled={loadingClusters || clusters.length===0}
                  className="w-full px-4 py-2 bg-gray-800 rounded-lg border border-gray-700 focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  {clusters.map((c) => (
                    <option key={c.id} value={c.id}>{c.name} - {c.type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-300">Cluster Role</label>
                <select
                  value={clusterRole}
                  onChange={(e)=>setClusterRole(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-800 rounded-lg border border-gray-700 focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="md:col-span-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={()=>setSelected(null)}
                  className="px-5 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                >
                  Reset / New
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg"
                >
                  {selected ? 'Save Changes' : 'Register'}
                </button>
              </div>
            </form>

            {/* Membership preview user terpilih */}
            {selected && (
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-gray-300 mb-2">Memberships</h3>
                <div className="space-y-1">
                  {(selected.memberships || []).length === 0 && (
                    <div className="text-gray-400 text-sm">Belum ada membership untuk user ini.</div>
                  )}
                  {(selected.memberships || []).map((m) => (
                    <div key={`${selected.id}-${m.cluster_id}`} className="text-sm text-gray-300">
                      <span className="px-2 py-0.5 rounded-full text-xs bg-cyan-900/30 border border-cyan-700/40 text-cyan-300 mr-2">
                        {m.cluster_name}
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-purple-900/30 border border-purple-700/40 text-purple-300">
                        {m.cluster_role}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ===== Delete Confirm Modal ===== */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => !deleting && setDeleteTarget(null)}
          />
          <div className="relative w-[92%] max-w-md rounded-xl border border-gray-700 bg-gray-900 p-5 shadow-2xl">
            <div className="text-lg font-semibold text-white">Delete User?</div>
      
            <div className="mt-2 text-sm text-gray-300">
              Kamu yakin mau menghapus user ini?
              <div className="mt-3 rounded-lg bg-gray-800 border border-gray-700 p-3">
                <div className="text-sm font-medium text-white">{deleteTarget.email}</div>
                <div className="text-xs text-gray-400">{deleteTarget.name || '—'}</div>
                <div className="mt-2 text-xs text-gray-400">
                  Role: <span className="text-gray-200">{deleteTarget.role}</span>
                </div>
              </div>
      
              <div className="mt-3 text-xs text-gray-400">
                Catatan: tombol ini membutuhkan endpoint backend <code className="text-gray-200">DELETE /api/admin/users/:id</code>.
              </div>
            </div>
      
            <div className="mt-5 flex justify-end gap-2">
              <button
                disabled={deleting}
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                disabled={deleting}
                onClick={() => deleteUser(deleteTarget)}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white disabled:opacity-60"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
                  
    </div>
  );
};

export default UserRegistrationForm;


























